import React, { useEffect, useMemo, useState } from "react"
import { Flex, Modal, Typography } from "@kohler-dsm-library/kohler-core"
import useViewport from "@/hooks/useViewport"
import CameraView from "@/components/VisualSearch/v1/CameraView"
import FileUpload from "@/components/GlobalVisualSearch/FileUpload"
import { useTranslation } from "next-i18next"
import { getTranslations } from "@/components/GlobalVisualSearch/localization"
import { useRouter } from "next/router"
import { useDispatch } from "react-redux"
import { updateVSResultData } from "@/store/features/vsresultSlice"
import backIcon from "@/public/icons/left-back-icon.svg"
import backIconWhite from "@/public/icons/back-arraow-white-icon.svg"
import CloseIconWhite from "@/public/icons/close-white.svg"
import qrCodeImage from "@/public/images/qrcode-scout.png"
import ErrorFallback from "@/components/GlobalVisualSearch/ErrorFallBack"
import CategorySection from "@/components/GlobalVisualSearch/CategorySection"
import ComingSoon from "@/components/GlobalVisualSearch/ComingSoon"
import LoadingSpinner from "@/components/GlobalVisualSearch/LoadingSpinner"
import { CONTACT_US_PAGE_URL_US } from "@/constants/index"
import Style from "@/components/GlobalVisualSearch/globalvisualsearch.module.scss"
import {
  productCategories,
  productSubCategories,
} from "@/components/GlobalVisualSearch/constant"
import SubcategorySection from "@/components/GlobalVisualSearch/SubcategorySection"

const GlobalVisualSearch = props => {
  const { openVisualSearch, setOpenVisualSearch } = props || {}
  const [openCam, setOpenCam] = useState(false)
  const [eventType, setEventType] = useState(null)
  const [image, setImage] = useState(null)
  const [category, setCategory] = useState("")
  const [section, setSection] = useState("category")
  const [output, setOutput] = useState(null)
  const { isSmallScreen, isSmallTabView, isExtraSmallScreen } = useViewport()
  const [isShowQr, setIShowQr] = useState(true)
  const router = useRouter()
  const dispatch = useDispatch()

  const { t } = useTranslation("common")
  const staticText = getTranslations(t)
  const {
    searchCategoryTitle = "",
    findCategory = () => {},
    selectProductTitle = "",
    selectTypeTitle = "",
    photoUploadTitle = "",
    dontSeeProduct = "",
    getHelp = "",
    haveTrouble = "",
    QRCodeText = "",
  } = staticText

  const isAtCategorySection = section === "category"
  const isAtSubCategorySection = section === "subCategory"
  const isAtUploadSection = section === "upload"
  const isAtCameraSection = section === "camera"
  const isAtResultSection = section === "result"
  const isAtErrorSection = section === "error"
  const isAtLoadingSection = section === "loading"
  const showCustomCloseBackIcon =
    isAtResultSection || isAtLoadingSection || isAtErrorSection

  const getCategoryTranslations = textKey => {
    const translatedText = staticText[textKey]
    return translatedText
  }

  const slectedCategoty = useMemo(() => {
    const combinedCategories = [
      ...(productCategories || []),
      ...(productSubCategories || []),
    ]
    return combinedCategories.find(d => d.value === category)?.name || ""
  }, [category])

  const isPreviosAtSubcategory = productSubCategories
    ?.map(d => d?.name)
    .includes(slectedCategoty)

  const clearSelection = () => {
    setCategory("")
    if (!isPreviosAtSubcategory) {
      setSection("category")
    } else {
      setSection("subCategory")
    }
    setIShowQr(true)
  }

  useEffect(() => {
    const inpageSearch = isExtraSmallScreen
      ? document.querySelector(".kf-react-button")
      : document.querySelector(".inpage-search__header-section")
    if (openCam && isSmallScreen) {
      document.body.style.overflow = "hidden"
      inpageSearch?.scrollIntoView({ behavior: "smooth" })
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [openCam])

  useEffect(() => {
    if (image) {
      const imageState = {}
      imageState.image = image?.[0] || image
      const dataResult = {
        ...imageState,
        category,
      }
      dispatch(updateVSResultData(dataResult))
      const url = new URL(window.location.href)
      const urlpathName = url?.pathname.includes("/result")
      if (!urlpathName) {
        setSection("loading-modal")
      } else {
        setOpenVisualSearch(false)
      }
      setOpenCam(false)
      router.push("/en/result")
    }
  }, [image])

  const renderSection = () => {
    switch (section) {
      case "category":
        return (
          <CategorySection
            setOpenCam={setOpenCam}
            setSection={setSection}
            setCategory={setCategory}
            eventType={eventType}
            category={category}
            output={output}
            setIShowQr={setIShowQr}
          />
        )
      case "subCategory":
        return (
          <SubcategorySection
            setOpenCam={setOpenCam}
            setSection={setSection}
            setCategory={setCategory}
            eventType={eventType}
            category={category}
            output={output}
            setIShowQr={setIShowQr}
          />
        )

      case "upload":
        return <FileUpload setImage={setImage} />

      case "camera":
        return (
          <div className="mobile-container">
            <CameraView
              setImage={setImage}
              setEventType={setEventType}
              back={clearSelection}
              category={category}
              setOpenCam={setOpenCam}
              setSection={setSection}
              setOutput={setOutput}
              isUsingGlobalVS
            />
          </div>
        )
      case "comingSoon":
        return <ComingSoon setSection={setSection} category={category} />
      case "loading-modal":
        return <LoadingSpinner />
      case "error":
        return <ErrorFallback category={category} />
      default:
        return (
          <CategorySection
            setOpenCam={setOpenCam}
            setSection={setSection}
            setCategory={setCategory}
          />
        )
    }
  }

  return (
    <div className={Style.GlobalVisualSearchContainer}>
      <Modal
        opened={openVisualSearch}
        onClose={() => {
          setOpenVisualSearch(false)
          setSection("category")
        }}
        data-testid="global-visualsearch-modal"
        className="global-visualsearch"
        modalContentProps={{ classname: "gvs-modal-content" }}
        closeButtonProps={{
          className:
            isAtCameraSection || showCustomCloseBackIcon
              ? "hide-close-btn"
              : "",
        }}
      >
        <div
          className={`${Style.ModalContent} ${
            showCustomCloseBackIcon && "p-0"
          } `}
          role="dialog"
          tabIndex="-1"
        >
          {!showCustomCloseBackIcon && (
            <div
              onClick={() => {
                if (isAtCategorySection) {
                  setOpenVisualSearch(false)
                } else {
                  if (!isPreviosAtSubcategory) {
                    setSection("category")
                  } else {
                    setSection("subCategory")
                  }
                  setCategory("")
                  setIShowQr(true)
                }
              }}
              className={Style.BackIconInMobileScreen}
            >
              {!(!isSmallScreen && isAtCategorySection) && (
                <img src={backIcon?.src} alt="back-arrow-icon" />
              )}
            </div>
          )}
          {isSmallScreen && showCustomCloseBackIcon && (
            <div className={Style.ResultPageNavigation}>
              <Flex align={"center"} justify={"space-between"}>
                <button onClick={() => setSection("camera")}>
                  <img src={backIconWhite.src} alt="back-arrow-icon-white" />
                </button>
                <button onClick={clearSelection}>
                  <img src={CloseIconWhite.src} alt="close-white-icon" />
                </button>
              </Flex>
            </div>
          )}
          {(isAtCategorySection ||
            isAtSubCategorySection ||
            isAtUploadSection) && (
            <>
              <Typography
                size={"h5"}
                className={`${Style.Title} search-text template`}
              >
                {!isAtUploadSection
                  ? searchCategoryTitle
                  : findCategory(getCategoryTranslations(slectedCategoty))}
              </Typography>
              <Typography
                size={"label-medium-l"}
                className={`${Style.SubTitle} search-text`}
              >
                {!isAtUploadSection
                  ? isAtCategorySection
                    ? selectProductTitle
                    : selectTypeTitle
                  : photoUploadTitle}
              </Typography>
            </>
          )}
          {renderSection()}

          {isShowQr ? (
            <Flex justify="space-between" align="center">
              <div className={Style.HelpSection}>
                <Typography size={"label-regular-s"}>
                  {!isAtUploadSection ? dontSeeProduct : haveTrouble}
                </Typography>
                <Typography size={"label-regular-s"}>
                  <a
                    href={CONTACT_US_PAGE_URL_US}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {getHelp}
                  </a>
                </Typography>
              </div>
              {!isSmallTabView ? (
                <div className={Style.QRSection}>
                  <Typography size={"title-s"}>{QRCodeText}</Typography>
                  <img src={qrCodeImage?.src} alt={QRCodeText} />
                </div>
              ) : null}
            </Flex>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}

export default GlobalVisualSearch
